/**
 * 「Mac に Biside を入れる」の言い方を、ここ 1 か所に置く。
 *
 * **写しを作らない。** 参加が済んだあとの画面（`/welcome`）と、Web アプリ側に元からある
 * 「Mac アプリで開く」の箱の、両方がこれを呼ぶ。この機能は「宛先が 2 か所」「指示が 2 か所」で
 * 既に事故を起こしているので、文面が 2 枚に増えた時点で片方が腐る。
 *
 * **なぜ「ダウンロード」ボタンではないのか。** 配っているアプリは自己署名で、公証を受けて
 * いない。ブラウザで `.zip` を落とすと隔離属性が付き、macOS 26 は「開発元を確認できないため
 * 開けません」で止める（右クリック→開く の逃げ道は廃止済み）。`curl` で落としたものには
 * 隔離属性が付かない —— **いま確実に入る道はこの 1 行だけ**なので、押すと詰むボタンを
 * 隣に並べない。
 */

import { esc } from "./board.ts";

/** サーバが持っている「入れ方の 1 行」。環境ごとに違う（本番と staging で置き場が違う）。 */
export async function installCommand(): Promise<string> {
  try {
    const res = await fetch("/api/bootstrap", { credentials: "same-origin" });
    if (!res.ok) return "";
    return ((await res.json()) as { installCommand?: string }).installCommand ?? "";
  } catch {
    return "";
  }
}

/** この画面を見ているのが Mac か。**iPad は Mac を名乗る**ので、触れる指の数で分ける。 */
export function isMac(): boolean {
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } };
  const platform = nav.userAgentData?.platform || navigator.platform || "";
  if (!/mac/i.test(platform)) return false;
  return !(navigator.maxTouchPoints > 1); // iPadOS は "MacIntel" を名乗る
}

/**
 * 入れ方の 3 手順。**畳んでおく。**
 *
 * 参加が済んだ画面で人がすることは 1 つ（アプリで開く）で、入れ方はその人が「押しても何も
 * 起きなかった」ときに初めて要る。最初から全部出しておくと、着地した瞬間の画面が手順書に
 * 見えて**全体が読み飛ばされる**（2026-08-22 の指摘）。畳んでおけば、要る人だけが開く。
 *
 * `<details>` を使うのは、開閉のために JavaScript を 1 行も書かなくてよいから
 * （キーボードでも開け、印刷にも出る）。中の [コピー] は開いていなくても繋がっている。
 */
export function installStepsHTML(cmd: string): string {
  if (!cmd) {
    return `
      <section class="wel-steps">
        <p class="wel-note">この場所では配布の案内を出せません。招いてくれた人にお尋ねください。</p>
      </section>`;
  }
  return `
    <details class="wel-steps">
      <summary>Mac に Biside を入れる</summary>
      <ol>
        <li>ターミナル（<span class="wel-key">⌘Space</span> →「ターミナル」）を開く</li>
        <li>この 1 行を貼って <span class="wel-key">Enter</span>
          <div class="wel-cmd"><code id="installCmd">${esc(cmd)}</code><button type="button" id="installCopy">コピー</button></div>
          <p class="wel-note">アプリを <code>/Applications</code> に入れます。走らせる中身は
            <a href="https://dl.biside.app/install.sh" target="_blank" rel="noreferrer">install.sh</a> で読めます。</p>
        </li>
        <li>入り終わったら、この画面に戻って上のボタンを押す</li>
      </ol>
    </details>`;
}

/**
 * コピーボタンを繋ぐ。**押したことが見える**ようにする（見えないと、人はもう一度押す）。
 * `navigator.clipboard` は安全でない文脈では使えないので、そのときは選んで渡す。
 */
export function wireInstall(root: ParentNode = document): void {
  const btn = root.querySelector<HTMLButtonElement>("#installCopy");
  const code = root.querySelector<HTMLElement>("#installCmd");
  if (!btn || !code) return;
  btn.addEventListener("click", async () => {
    const text = code.textContent ?? "";
    let copied = false;
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch {
      const range = document.createRange();
      range.selectNodeContents(code);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    btn.textContent = copied ? "コピーしました" : "選びました（⌘C）";
    btn.classList.add("done");
    setTimeout(() => {
      btn.textContent = "コピー";
      btn.classList.remove("done");
    }, 2000);
  });
}
